import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { isBuilt as isFormBuilt } from '../lib/hostedForm';
import PortalToggle from './PortalToggle';
import ConfirmDialog from './ConfirmDialog';
import MoreMenu from './MoreMenu';
import { isAdmin } from '../lib/currentUser';
import UploadDocumentModal from './UploadDocumentModal';
import ManageCategoriesPanel from './ManageCategoriesPanel';
import DocumentConfigModal from './DocumentConfigModal';
import { useActionToast } from './ActionToast';
import { notifyDocumentToSuppliers } from '../lib/notifyDocument';
import { recomputeEcoBadge } from '../lib/ecoAward';
import { fmtDate } from '../lib/dates';

// Fusion Documents — document library with four sections:
//
//   1. Terms & Conditions      — versioned, portal-visible by default
//   2. Compliance              — visibility set by Risk Flag Config
//   3. Supplier Enhancement    — per-doc portal toggle
//   4. Internal Reference      — admin-only, never visible in portal
//
// Compliance docs' "linked-to" indicator is derived from
// risk_document_requirements (the matrix shipped on the Risk Flag tab).
// Other sections derive their indicator from category + role.
//
// State stays local — search, filter, expanded version history, the
// config modal, and the three confirmation dialogs (T&C hide, category
// change, restore-as-active).

const CATEGORY_LABEL = {
  terms_conditions:     'Terms & Conditions',
  compliance:           'Compliance',
  supplier_enhancement: 'Supplier Enhancement',
  internal_reference:   'Internal Reference',
};
const SECTION_ORDER = ['terms_conditions', 'compliance', 'supplier_enhancement', 'internal_reference'];
const SECTION_META = {
  terms_conditions: {
    accent: '#92D400',
    sub: 'Active version shown to suppliers at sign-up',
  },
  compliance: {
    accent: '#2E8FA1',
    sub: 'Visibility is set by risk tier — manage in Risk Flag Configuration',
  },
  supplier_enhancement: {
    accent: '#92D400',
    sub: 'Optional documents · use the portal toggle to control visibility',
  },
  internal_reference: {
    accent: '#51534A',
    sub: 'For team use only — never shown to suppliers',
  },
};
// Section accent + sub-line derived from a category's BEHAVIOUR model, for
// categories that aren't one of the original four (which keep SECTION_META).
const META_BY_BEHAVIOUR = {
  shown:    { accent: '#92D400', sub: 'Shown to suppliers · use the portal toggle per document' },
  required: { accent: '#2E8FA1', sub: 'Visibility is set by risk tier — manage in Risk Flag Configuration' },
  internal: { accent: '#51534A', sub: 'For team use only — never shown to suppliers' },
};
const STATUS_PILL = {
  active:     { label: 'Active',     classes: 'text-fusion-forest bg-[#92D400]/10 border-[#92D400]/30' },
  draft:      { label: 'Draft',      classes: 'text-[#7A4E10] bg-[#E89A2E]/10 border-[#E89A2E]/30' },
  superseded: { label: 'Superseded', classes: 'text-[#51534A] bg-[#51534A]/10 border-[#DADCCD]/30' },
  archived:   { label: 'Archived',   classes: 'text-[#51534A] bg-[#51534A]/10 border-[#DADCCD]/30' },
};
const TIER_LABEL = { high: 'High', medium: 'Medium', low: 'Low' };

// Compose the "Required: …" linked-to label for compliance docs from the
// set of tiers that require this document_key in risk_document_requirements.
// 'All tiers' collapses to a shorter label so the chip stays compact.
// How a compliance document is satisfied — shown as a pill on each compliance
// row so migration off paper is auditable at a glance (and filterable).
const METHOD_PILL = {
  upload:          { label: 'Supplier uploads',  cls: 'text-[#2E8FA1] bg-[#2E8FA1]/10 border-[#2E8FA1]/30' },
  download_return: { label: 'Download & return', cls: 'text-[#7A4E10] bg-[#E89A2E]/10 border-[#E89A2E]/40' },
  form:            { label: 'Hosted form',        cls: 'text-fusion-forest bg-[#92D400]/10 border-[#92D400]/40' },
  acknowledge:     { label: 'Acknowledge',        cls: 'text-[#51534A] bg-[#51534A]/10 border-[#DADCCD]' },
  // pdf_sign was added as the fifth fulfilment method but never added here, so
  // those documents rendered NO method chip at all — the row simply had a gap
  // where every other document names how it's satisfied. It also never appeared
  // in the method filter, so there was no way to audit them as a group.
  pdf_sign:        { label: 'Sign on PDF',        cls: 'text-[#5B3FA8] bg-[#5B3FA8]/10 border-[#5B3FA8]/30' },
};
const METHOD_ORDER = ['upload', 'download_return', 'form', 'acknowledge', 'pdf_sign'];

const formatRequiredTiers = (tiers) => {
  if (!tiers || tiers.length === 0) return 'Required: none';
  if (tiers.length === 3) return 'Required: All tiers';
  return 'Required: ' + tiers.map(t => TIER_LABEL[t] || t).join(' & ');
};

const formatFileSize = (kb) => {
  if (kb == null) return null;
  if (kb < 1024) return `${kb} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const versionLabel = (doc) => doc.version_value
  ? `v${doc.version_value}${doc.version_year ? ` · ${doc.version_year}` : ''}`
  : null;

export default function FusionDocuments({ currentUser, onOpenRiskFlagConfig }) {
  const [docs, setDocs] = useState([]);
  const [requirementsByKey, setRequirementsByKey] = useState({}); // compliance_doc_key → ['high','medium','low']
  const [loaded, setLoaded] = useState(false);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all'); // compliance method audit filter
  const [statusFilter, setStatusFilter] = useState('all'); // active | draft | archived | all. Defaults to All so a just-saved draft (required docs can only be drafts here) doesn't vanish; filter down if wanted.
  const [showVisibilityHelp, setShowVisibilityHelp] = useState(false); // §6 — banner behind a link
  const [expandedFor, setExpandedFor] = useState(null);          // doc.id whose version history is open

  const [configDocId, setConfigDocId] = useState(null);
  const [confirmTcHide, setConfirmTcHide] = useState(null);
  const [confirmCategoryChange, setConfirmCategoryChange] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [confirmRestoreArchive, setConfirmRestoreArchive] = useState(null);
  // Permanent delete — admins / super users only (Archive is the soft option
  // everyone gets). Gated via isAdmin; the menu item is hidden otherwise.
  const [confirmDelete, setConfirmDelete] = useState(null);
  const canDelete = isAdmin(currentUser);
  const [showUpload, setShowUpload] = useState(false);
  const [showManageCats, setShowManageCats] = useState(false);
  const [categories, setCategories] = useState([]);
  const [badgeCats, setBadgeCats] = useState([]);
  const [rulesByKey, setRulesByKey] = useState({}); // compliance_doc_key → [reminder rule]

  // All confirmations go through the shared action toast (unified 2026-08-07).
  // flash() keeps its call sites; it just delegates now.
  const { notify, Toast } = useActionToast();
  const flash = useCallback(
    (msg, tone = 'green') => notify({ title: msg, tone: tone === 'red' ? 'danger' : 'success' }, { holdMs: tone === 'red' ? 6000 : 4000 }),
    [notify],
  );

  // ============ Loaders ============

  const loadAll = useCallback(async () => {
    const [{ data: docsData }, { data: reqs }, { data: cats }, { data: bcats }, { data: rulesData }] = await Promise.all([
      supabase
        .from('fusion_documents')
        .select('*')
        .order('document_name'),
      supabase.from('risk_document_requirements').select('risk_level, document_key'),
      supabase.from('document_categories').select('key, label, behaviour, sort_order, badge_category_id, badge_tier_awarded, portal_coming_soon').order('sort_order'),
      supabase.from('badge_categories').select('id, slug, name').eq('is_active', true).order('name'),
      supabase.from('document_reminder_rules').select('*'),
    ]);
    setDocs(docsData || []);
    setCategories(cats || []);
    setBadgeCats(bcats || []);
    const byKey = {};
    for (const r of (reqs || [])) {
      if (!byKey[r.document_key]) byKey[r.document_key] = [];
      byKey[r.document_key].push(r.risk_level);
    }
    setRequirementsByKey(byKey);
    const rByKey = {};
    for (const r of (rulesData || [])) {
      if (!rByKey[r.document_key]) rByKey[r.document_key] = [];
      rByKey[r.document_key].push(r);
    }
    setRulesByKey(rByKey);
    setLoaded(true);
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ============ Derived view data ============

  const filteredDocs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter(d => {
      if (!q) return true;
      const hay = `${d.document_name || ''} ${CATEGORY_LABEL[d.document_type] || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [docs, search]);

  // Group docs by section, then split into "main list" (active/draft) and
  // "version history" (superseded children, grouped under their active
  // parent by parent_document_id). Archived rows go to the bottom group.
  const grouped = useMemo(() => {
    const out = { archived: [] };
    for (const section of SECTION_ORDER) out[section] = [];

    const supersededByParent = {};
    // An in-progress DRAFT of a live document (document-versioning §3a) is not
    // a document of its own — it's an unpublished edit of the row above it. It
    // belongs INSIDE its parent's row, not beside it as a duplicate.
    const draftByParent = {};
    for (const d of filteredDocs) {
      if (d.status === 'superseded' && d.parent_document_id) {
        if (!supersededByParent[d.parent_document_id]) supersededByParent[d.parent_document_id] = [];
        supersededByParent[d.parent_document_id].push(d);
      }
      if (d.status === 'draft' && d.parent_document_id) {
        draftByParent[d.parent_document_id] = d;
      }
    }

    // Status filter (§3) — Active is the default; Archived / Drafts / All are
    // chosen from the same control that used to be a separate "View archived".
    const statusOk = (s) => statusFilter === 'all'
      ? (s === 'active' || s === 'draft' || s === 'archived')
      : s === statusFilter;

    for (const d of filteredDocs) {
      if (filter !== 'all' && d.document_type !== filter) continue;
      // Method filter is a compliance-only audit lens: it hides non-compliance
      // docs and keeps only the chosen fulfilment method.
      if (methodFilter !== 'all' && (!d.compliance_doc_key || (d.satisfied_by || 'upload') !== methodFilter)) continue;
      if (d.status === 'superseded') continue; // rendered inside its parent's history
      if (d.status === 'draft' && d.parent_document_id && draftByParent[d.parent_document_id]) {
        continue; // an unpublished edit of a live doc — shown on the parent row
      }
      if (!statusOk(d.status)) continue;
      if (d.status === 'archived') {
        out.archived.push({ ...d, _olderVersions: supersededByParent[d.id] || [] });
        continue;
      }
      const bucket = d.document_type || 'internal_reference';
      if (!out[bucket]) out[bucket] = [];
      out[bucket].push({ ...d, _olderVersions: supersededByParent[d.id] || [], _workingDraft: draftByParent[d.id] || null });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredDocs, filter, methodFilter, statusFilter]);

  const configDoc = useMemo(() => docs.find(d => d.id === configDocId) || null, [docs, configDocId]);

  // Data-driven categories: order + labels + behaviour come from the
  // document_categories table (fallback to the original hard-coded four so the
  // panel renders before the table loads / migration runs).
  const categoryByKey = useMemo(() => new Map(categories.map(c => [c.key, c])), [categories]);
  const sectionOrder = categories.length ? categories.map(c => c.key) : SECTION_ORDER;
  const catLabel = (key) => categoryByKey.get(key)?.label || CATEGORY_LABEL[key] || key;
  const catMeta = (key) => SECTION_META[key] || META_BY_BEHAVIOUR[categoryByKey.get(key)?.behaviour] || { accent: '#51534A', sub: '' };
  const catBehaviour = (key) => categoryByKey.get(key)?.behaviour
    || (key === 'compliance' ? 'required' : key === 'internal_reference' ? 'internal' : 'shown');

  // Result count (§5): filtered visible documents against the unfiltered total
  // (excluding superseded version-history rows, which aren't standalone docs).
  const totalDocs = useMemo(() => docs.filter(d => d.status !== 'superseded').length, [docs]);
  const visibleCount = useMemo(() => Object.values(grouped).reduce((n, arr) => n + arr.length, 0), [grouped]);

  // Active-filter chips (§4) — one per applied filter, each removable; Active
  // status is the default so it isn't a chip. Only rendered when non-empty.
  const STATUS_LABEL = { active: 'Active', draft: 'Drafts', archived: 'Archived', all: 'All statuses' };
  const activeChips = useMemo(() => {
    const chips = [];
    if (filter !== 'all') chips.push({ key: 'category', label: catLabel(filter), clear: () => setFilter('all') });
    if (methodFilter !== 'all') chips.push({ key: 'method', label: METHOD_PILL[methodFilter]?.label || methodFilter, clear: () => setMethodFilter('all') });
    if (statusFilter !== 'all') chips.push({ key: 'status', label: STATUS_LABEL[statusFilter], clear: () => setStatusFilter('all') });
    if (search.trim()) chips.push({ key: 'search', label: `“${search.trim()}”`, clear: () => setSearch('') });
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, methodFilter, statusFilter, search]);
  const filtersActive = activeChips.length > 0;
  const clearAllFilters = () => { setFilter('all'); setMethodFilter('all'); setStatusFilter('all'); setSearch(''); };

  // ============ Save / state mutations ============

  const saveDocFields = async ({ fields, reminderRules, publish, supplierIds }) => {
    if (!configDoc) return;
    // Detect a visibility-implicating category move BEFORE writing.
    // internal_reference → anything else widens reach to suppliers, so
    // we close the modal and queue a ConfirmDialog rather than commit
    // silently. The opposite direction (→ internal_reference) just
    // surfaces a toast on success — hiding doesn't need a confirm.
    const movingOutOfInternal = configDoc.document_type === 'internal_reference'
      && fields.document_type !== 'internal_reference';
    const movingIntoInternal = configDoc.document_type !== 'internal_reference'
      && fields.document_type === 'internal_reference';
    if (movingOutOfInternal) {
      setConfirmCategoryChange({ docId: configDoc.id, fields });
      setConfigDocId(null);
      return;
    }

    // Internal Reference forces visible_in_portal=false at the DB layer
    // (CHECK constraint), so coerce client-side too. badge_* columns
    // must be set or cleared together — the DB CHECK enforces co-
    // presence, so we pass through whatever the modal returned (either
    // both null or both set).
    // Acknowledge config — only the compliance docs carry these
    // fields. For everything else we coerce satisfied_by back to
    // 'upload' + null the Acknowledge-only fields so a doc that
    // gets re-categorised away from compliance doesn't carry stale
    // Acknowledge config on the row.
    // Behaviour-driven now (data-driven categories): any Required category is
    // "compliance", not just the seed 'compliance' key.
    const isComplianceNow = catBehaviour(fields.document_type) === 'required';
    const satisfiedBy = isComplianceNow ? (fields.satisfied_by || 'upload') : 'upload';
    // Preserve the actual method for non-acknowledge docs (upload /
    // download_return / form) — only 'acknowledge' carries lifecycle/source/
    // content. Hardcoding 'upload' here previously clobbered download_return.
    const ackConfig = satisfiedBy === 'acknowledge'
      ? {
          satisfied_by: 'acknowledge',
          lifecycle: fields.lifecycle,
          source: fields.source,
          content_richtext: fields.source === 'richtext' ? (fields.content_richtext || null) : null,
          content_file_url: fields.source === 'file' ? (fields.content_file_url || null) : null,
          content_file_name: fields.source === 'file' ? (fields.content_file_name || null) : null,
        }
      : {
          satisfied_by: satisfiedBy,
          lifecycle: null,
          source: null,
          content_richtext: null,
          content_file_url: null,
          content_file_name: null,
        };

    // Compliance config columns (Required docs only) — the modal merged these
    // into `fields` via normaliseComplianceFields. Non-compliance saves leave
    // them untouched.
    const complianceCols = isComplianceNow ? {
      validity_value: fields.validity_value ?? null,
      validity_unit: fields.validity_unit || 'months',
      expiring_soon_value: fields.expiring_soon_value ?? 30,
      expiring_soon_unit: fields.expiring_soon_unit || 'days',
      supplier_provides_expiry: !!fields.supplier_provides_expiry,
      accepted_file_types: fields.accepted_file_types || ['pdf'],
      max_file_size_mb: fields.max_file_size_mb ?? 10,
      block_expired_from_briefs: !!fields.block_expired_from_briefs,
      auto_flag_red_when_expired: !!fields.auto_flag_red_when_expired,
    } : {};

    // Recognition award is category-driven — always stamp from the (possibly
    // just-changed) category so a recategorise re-inherits correctly.
    const selCat = categoryByKey.get(fields.document_type);

    const patch = {
      document_name: fields.document_name,
      document_type: fields.document_type,
      description: fields.description,
      visible_in_portal: fields.document_type === 'internal_reference' ? false : !!fields.visible_in_portal,
      notify_on_update: !!fields.notify_on_update,
      badge_category_id: selCat?.badge_category_id || null,
      badge_tier_awarded: selCat?.badge_tier_awarded || null,
      ...ackConfig,
      ...complianceCols,
      // Publishing a draft from Configure flips it live.
      ...(publish ? { status: 'active', is_active: true } : {}),
    };

    const { error: updErr } = await supabase
      .from('fusion_documents')
      .update(patch)
      .eq('id', configDoc.id);
    if (updErr) throw new Error(updErr.message);

    // Auto-reminder rules (Required docs) — wipe + reinsert keyed by
    // compliance_doc_key, mirroring the upload flow.
    if (isComplianceNow && reminderRules && configDoc.compliance_doc_key) {
      await supabase.from('document_reminder_rules').delete().eq('document_key', configDoc.compliance_doc_key);
      if (reminderRules.length > 0) {
        await supabase.from('document_reminder_rules').insert(reminderRules.map(r => ({
          document_key: configDoc.compliance_doc_key,
          timing_value: r.timing_value || 0,
          timing_unit: r.timing_unit || 'days',
          timing_direction: r.timing_direction,
          cadence: r.cadence || 'one_time',
          created_by: currentUser?.id || null,
        })));
      }
    }

    // Publish side-effects (draft → live from Configure): eco ripple for an
    // eco-linked doc + supplier notification. Mirrors the Upload modal.
    let notifyResult = null;
    if (publish) {
      const ecoCat = badgeCats.find(b => b.slug === 'eco');
      if (ecoCat && selCat?.badge_category_id === ecoCat.id) {
        const { data: sups } = await supabase.from('suppliers').select('id, risk_flag').eq('status', 'active');
        for (const s of (sups || [])) {
          try { await recomputeEcoBadge(supabase, s.id, s.risk_flag || null); } catch { /* skip */ }
        }
      }
      if (publish === 'notify' || publish === 'test') {
        try {
          notifyResult = await notifyDocumentToSuppliers({
            documentLabel: fields.document_name,
            sentByUserId: currentUser?.id || null,
            testEmail: publish === 'test' ? (currentUser?.email || 'robynne.elliott@fusion-events.com') : null,
            supplierIds: publish === 'notify' ? supplierIds : null,
          });
        } catch { /* surfaced as a plain publish below */ }
      }
    }

    flash(
      movingIntoInternal ? 'Document moved to Internal Reference and hidden from supplier portal.'
      : notifyResult?.test ? `Published — test email sent to ${notifyResult.testEmail}. Suppliers were NOT notified.`
      : notifyResult ? `Published — notified ${notifyResult.suppliersLogged} supplier(s).`
      : publish ? 'Document published.'
      : 'Saved');
    await loadAll();
    setConfigDocId(null);
  };

  // Confirmed category-change second step. Repeats the save with the
  // patch the modal queued, this time without bailing on the confirmation.
  const commitCategoryChange = async () => {
    const c = confirmCategoryChange;
    setConfirmCategoryChange(null);
    if (!c) return;
    const patch = {
      document_name: c.fields.document_name,
      document_type: c.fields.document_type,
      description: c.fields.description,
      visible_in_portal: c.fields.document_type === 'internal_reference' ? false : !!c.fields.visible_in_portal,
      notify_on_update: !!c.fields.notify_on_update,
      badge_category_id: c.fields.badge_category_id || null,
      badge_tier_awarded: c.fields.badge_tier_awarded || null,
    };
    const { error: updErr } = await supabase
      .from('fusion_documents')
      .update(patch)
      .eq('id', c.docId);
    if (updErr) { flash(`Could not save: ${updErr.message}`, 'red'); return; }
    flash('Category changed — document is now visible to suppliers.');
    await loadAll();
    setConfigDocId(null);
  };

  // Inline toggle on a row. Optimistic — flip local state first so the
  // UI feels immediate, then revert on DB error.
  const toggleVisibility = async (doc, next) => {
    // T&Cs hiding gets a warning; rest just write.
    if (doc.document_type === 'terms_conditions' && doc.status === 'active' && !next) {
      setConfirmTcHide(doc);
      return;
    }
    setDocs(prev => prev.map(d => d.id === doc.id ? { ...d, visible_in_portal: next } : d));
    const { error: updErr } = await supabase
      .from('fusion_documents')
      .update({ visible_in_portal: next })
      .eq('id', doc.id);
    if (updErr) {
      flash(`Could not update visibility: ${updErr.message}`, 'red');
      setDocs(prev => prev.map(d => d.id === doc.id ? { ...d, visible_in_portal: !next } : d));
      return;
    }
    flash(next ? 'Visible in portal' : 'Hidden from portal');
  };

  const commitTcHide = async () => {
    const doc = confirmTcHide;
    setConfirmTcHide(null);
    if (!doc) return;
    const { error: updErr } = await supabase
      .from('fusion_documents')
      .update({ visible_in_portal: false })
      .eq('id', doc.id);
    if (updErr) { flash(`Could not hide: ${updErr.message}`, 'red'); return; }
    flash('T&Cs hidden from portal — supplier sign-up flow is broken until re-enabled.', 'red');
    await loadAll();
  };

  // Restore-as-Active swaps statuses between the chosen version (currently
  // superseded) and its parent (currently active). Two updates, no
  // transaction wrapper — worst case we end up with two actives briefly
  // which the next page-load corrects.
  const commitRestoreVersion = async () => {
    const target = confirmRestore;
    setConfirmRestore(null);
    if (!target) return;
    const parent = docs.find(d => d.id === target.parent_document_id);
    if (!parent) { flash('Could not find parent version.', 'red'); return; }
    const now = new Date().toISOString();
    const { error: e1 } = await supabase
      .from('fusion_documents')
      .update({ status: 'superseded', superseded_at: now, superseded_by_id: target.id })
      .eq('id', parent.id);
    if (e1) { flash(`Could not supersede current version: ${e1.message}`, 'red'); return; }
    const { error: e2 } = await supabase
      .from('fusion_documents')
      .update({ status: 'active', superseded_at: null, superseded_by_id: null })
      .eq('id', target.id);
    if (e2) { flash(`Could not restore: ${e2.message}`, 'red'); return; }
    flash(`Restored ${versionLabel(target) || 'version'} as Active.`);
    await loadAll();
  };

  const archiveDoc = async () => {
    const doc = confirmArchive;
    setConfirmArchive(null);
    if (!doc) return;
    const { error: updErr } = await supabase
      .from('fusion_documents')
      .update({ status: 'archived', is_active: false, archived_at: new Date().toISOString() })
      .eq('id', doc.id);
    if (updErr) { flash(`Could not archive: ${updErr.message}`, 'red'); return; }
    flash(`"${doc.document_name}" archived.`);
    await loadAll();
  };
  const restoreFromArchive = async () => {
    const doc = confirmRestoreArchive;
    setConfirmRestoreArchive(null);
    if (!doc) return;
    const { error: updErr } = await supabase
      .from('fusion_documents')
      .update({ status: 'active', is_active: true, archived_at: null })
      .eq('id', doc.id);
    if (updErr) { flash(`Could not restore: ${updErr.message}`, 'red'); return; }
    flash(`"${doc.document_name}" restored.`);
    await loadAll();
  };

  // Permanent delete (admin / super only). Removes the document row and any
  // superseded version children — version children first so a parent FK can't
  // block the parent delete. Unlike Archive this can't be undone.
  const deleteDoc = async () => {
    const doc = confirmDelete;
    setConfirmDelete(null);
    if (!doc) return;
    const { error: childErr } = await supabase.from('fusion_documents').delete().eq('parent_document_id', doc.id);
    const { error: delErr } = childErr ? { error: childErr } : await supabase.from('fusion_documents').delete().eq('id', doc.id);
    if (delErr) {
      // A version with signatures against it is protected at the DB by the
      // signature FKs (ON DELETE RESTRICT) — document-versioning §3a keeps
      // superseded versions indefinitely, because deleting one destroys the
      // evidence for every signature taken against it. Say that, rather than
      // showing the raw constraint error.
      const blockedBySignatures = /foreign key|violates|23503/i.test(delErr.message || '');
      flash(
        blockedBySignatures
          ? `"${doc.document_name}" can't be deleted — suppliers have signed a version of it, and those signatures need the document to stay. Archive it instead.`
          : `Could not delete: ${delErr.message}`,
        'red',
      );
      return;
    }
    flash(`"${doc.document_name}" deleted.`);
    await loadAll();
  };

  // ============ Render ============

  return (
    <div>
      {/* ===== Actions row =====
          The duplicate "Fusion Documents" section heading + description are gone
          (docs-filters §7) — the tab already names where you are, and Risk Flag
          Config is still reachable from the visibility banner below. */}
      <div className="flex justify-end items-center gap-2 mb-4">
        <div className="flex gap-2 flex-shrink-0">
          {canDelete && (
            <button
              type="button"
              onClick={() => setShowManageCats(true)}
              className="font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase bg-white border border-line rounded px-4 py-2 text-ink hover:border-[#DADCCD] transition-colors">
              Manage Categories
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowUpload(true)}
            className="font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase bg-[#92D400] border border-[#92D400] rounded px-4 py-2 text-[#131313] hover:bg-[#a4e810] transition-colors flex items-center gap-2">
            <PlusIcon /> New document
          </button>
        </div>
      </div>

      {/* ===== Filter bar (docs-filters §1) — one row: search sits apart, then
          three selects (category is unbounded so it must be a select), then the
          result count. Category/method pills are retired. ===== */}
      <div role="search" aria-label="Filter documents" className="flex gap-2.5 items-center mb-2.5 flex-wrap">
        <div className="relative flex-1 max-w-[320px] min-w-[200px]">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#51534A] pointer-events-none">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" strokeLinecap="round" /></svg>
          </span>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search documents"
            placeholder="Search documents"
            className="w-full bg-white border border-line rounded-lg pl-9 pr-3 py-2 text-ink font-['Metropolis'] text-[13px] outline-none focus:border-[#DADCCD] transition-colors" />
        </div>
        <FilterSelect value={filter} onChange={setFilter} active={filter !== 'all'} ariaLabel="Filter by category">
          <option value="all">All categories</option>
          {sectionOrder.map(cat => <option key={cat} value={cat}>{catLabel(cat)}</option>)}
        </FilterSelect>
        <FilterSelect value={methodFilter} onChange={setMethodFilter} active={methodFilter !== 'all'} ariaLabel="Filter by method">
          <option value="all">All methods</option>
          {METHOD_ORDER.map(m => <option key={m} value={m}>{METHOD_PILL[m].label}</option>)}
        </FilterSelect>
        <FilterSelect value={statusFilter} onChange={setStatusFilter} active={statusFilter !== 'active'} ariaLabel="Filter by status">
          <option value="active">Active</option>
          <option value="draft">Drafts</option>
          <option value="archived">Archived</option>
          <option value="all">All statuses</option>
        </FilterSelect>
        <div className="ml-auto flex items-center gap-4">
          <button
            type="button"
            onClick={() => setShowVisibilityHelp(v => !v)}
            aria-expanded={showVisibilityHelp}
            className="font-['Metropolis'] text-[12px] font-semibold text-[#2E8FA1] hover:underline bg-transparent border-0 p-0 cursor-pointer whitespace-nowrap">
            How visibility works
          </button>
          <span aria-live="polite" className="font-['Metropolis'] text-[13px] text-[#51534A] whitespace-nowrap">
            {visibleCount} of {totalDocs} document{totalDocs === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {/* Visibility explanation (§6) — reference material, behind the link above
          instead of permanently occupying space over every document. */}
      {showVisibilityHelp && (
        <div className="bg-[#2E8FA1]/[0.06] border border-[#2E8FA1]/25 rounded-lg p-3.5 mb-3 flex items-start gap-3">
          <span className="text-[#2E8FA1] flex-shrink-0 mt-0.5"><InfoIcon /></span>
          <div className="flex-1 min-w-0 font-['Metropolis'] text-xs text-fusion-mid-grey leading-relaxed">
            <strong className="text-ink font-semibold">How visibility works:</strong> A category's behaviour sets it. <strong className="text-ink">Shown</strong> categories use a per-document portal toggle. <strong className="text-ink">Required</strong> (compliance) categories are shown to suppliers by risk tier — set in Risk Flag Configuration. <strong className="text-ink">Internal</strong> categories are never shown to suppliers.
          </div>
          <button
            type="button"
            onClick={() => onOpenRiskFlagConfig?.()}
            className="font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase text-[#2E8FA1] hover:text-[#3aa8bd] bg-transparent border-none p-0 cursor-pointer whitespace-nowrap">
            Open Risk Flag Config →
          </button>
        </div>
      )}

      {/* Active-filter chips (§4) — only render when something is applied. */}
      {filtersActive && (
        <div className="flex items-center gap-2 flex-wrap mb-3.5">
          {activeChips.map(chip => (
            <span key={chip.key} className="inline-flex items-center gap-1.5 font-['Metropolis'] text-[12px] font-semibold bg-[#92D400]/10 border border-[#92D400]/40 text-fusion-forest rounded-full pl-3 pr-1.5 py-1">
              {chip.label}
              <button
                type="button"
                onClick={chip.clear}
                aria-label={`Remove ${chip.label} filter`}
                className="text-fusion-forest/70 hover:text-fusion-forest leading-none text-[15px] px-1 bg-transparent border-0 cursor-pointer">×</button>
            </span>
          ))}
          <button
            type="button"
            onClick={clearAllFilters}
            className="font-['Metropolis'] text-[12px] font-semibold text-[#2E8FA1] hover:underline bg-transparent border-0 cursor-pointer">
            Clear filters
          </button>
        </div>
      )}

      {/* ===== Sections ===== */}
      {!loaded ? (
        <p className="font-['Metropolis'] text-sm text-[#51534A]">Loading…</p>
      ) : (filtersActive && visibleCount === 0) ? (
        // Cause-aware empty state (§5): a filter is hiding everything — distinct
        // from a genuinely empty category (which shows its own per-section note).
        <div className="bg-white border border-line rounded-xl px-8 py-12 text-center">
          <p className="font-['Metropolis'] text-base font-semibold text-ink">No documents match these filters</p>
          <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mt-1">Try widening or clearing them to see the rest of the library.</p>
          <button type="button" onClick={clearAllFilters}
            className="mt-4 font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase text-fusion-forest border border-[#92D400] rounded-full px-5 py-2 hover:bg-[#92D400]/10 transition-colors">
            Clear filters
          </button>
        </div>
      ) : (
        <>
          {sectionOrder.map(section => {
            if (filter !== 'all' && filter !== section) return null;
            return (
              <SectionBlock
                key={section}
                section={section}
                label={catLabel(section)}
                meta={catMeta(section)}
                behaviour={catBehaviour(section)}
                docs={grouped[section] || []}
                requirementsByKey={requirementsByKey}
                expandedFor={expandedFor}
                onToggleExpand={(id) => setExpandedFor(prev => prev === id ? null : id)}
                onConfigure={(doc) => setConfigDocId(doc.id)}
                onToggleVisibility={toggleVisibility}
                onArchive={(doc) => setConfirmArchive(doc)}
                onDelete={canDelete ? (doc) => setConfirmDelete(doc) : null}
                onRestoreVersion={(version) => setConfirmRestore(version)}
                onOpenRiskFlagConfig={onOpenRiskFlagConfig}
                onFlash={flash}
                isActiveConfigDoc={configDocId} />
            );
          })}

          {/* Archived — shown when the Status filter includes them (§3). */}
          {grouped.archived.length > 0 && (
            <SectionBlock
              section="archived"
              label="Archived"
              meta={{ accent: '#51534A', sub: 'Archived documents — hidden from the main library' }}
              behaviour="internal"
              docs={grouped.archived}
              requirementsByKey={requirementsByKey}
              expandedFor={expandedFor}
              onToggleExpand={(id) => setExpandedFor(prev => prev === id ? null : id)}
              onConfigure={(doc) => setConfigDocId(doc.id)}
              onToggleVisibility={() => {}}
              onArchive={() => {}}
              onDelete={canDelete ? (doc) => setConfirmDelete(doc) : null}
              onRestoreArchive={(doc) => setConfirmRestoreArchive(doc)}
              onRestoreVersion={() => {}}
              onOpenRiskFlagConfig={onOpenRiskFlagConfig}
              onFlash={flash}
              isActiveConfigDoc={configDocId} />
          )}
        </>
      )}

      {/* Shared action toast (bottom-right, stacked). */}
      <Toast />

      {/* ===== Config modal ===== */}
      <DocumentConfigModal
        open={!!configDocId && !!configDoc}
        document={configDoc}
        categories={categories}
        reminderRules={configDoc ? (rulesByKey[configDoc.compliance_doc_key] || []) : []}
        onClose={() => setConfigDocId(null)}
        onSave={saveDocFields} />

      {/* ===== T&C hide warning ===== */}
      <ConfirmDialog
        open={!!confirmTcHide}
        title="Hide active T&Cs?"
        body="Hiding T&Cs will break the supplier sign-up flow. New suppliers won't be able to complete onboarding until you turn this back on or replace it with another active version. Continue?"
        confirmLabel="Hide T&Cs"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={commitTcHide}
        onCancel={() => setConfirmTcHide(null)} />

      {/* ===== Category change (out of internal_reference) ===== */}
      <ConfirmDialog
        open={!!confirmCategoryChange}
        title="Make this document visible to suppliers?"
        body="This will move the document out of Internal Reference. Suppliers may be able to see it in their portal depending on the new category's visibility rules. Confirm?"
        confirmLabel="Change category"
        cancelLabel="Keep as Internal"
        onConfirm={commitCategoryChange}
        onCancel={() => setConfirmCategoryChange(null)} />

      {/* ===== Restore version as active ===== */}
      <ConfirmDialog
        open={!!confirmRestore}
        title={confirmRestore ? `Restore ${versionLabel(confirmRestore) || 'this version'} as Active?` : ''}
        body="Any suppliers signed since the current version went active may need to acknowledge the restored version. The currently active version is moved to history."
        confirmLabel="Restore as Active"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={commitRestoreVersion}
        onCancel={() => setConfirmRestore(null)} />

      {/* ===== Archive ===== */}
      <ConfirmDialog
        open={!!confirmArchive}
        title={confirmArchive ? `Archive "${confirmArchive.document_name}"?` : ''}
        body="Archived documents stay in the library but are hidden from suppliers and admins. Restore from View Archived if you need them back."
        confirmLabel="Archive"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={archiveDoc}
        onCancel={() => setConfirmArchive(null)} />

      {/* ===== Delete (admin / super only) ===== */}
      <ConfirmDialog
        open={!!confirmDelete}
        title={confirmDelete ? `Delete "${confirmDelete.document_name}"?` : ''}
        body="This permanently removes the document and its version history. It can't be undone — if you only want to hide it from suppliers, use Archive instead."
        confirmLabel="Delete permanently"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={deleteDoc}
        onCancel={() => setConfirmDelete(null)} />

      {/* ===== Restore from archive ===== */}
      <ConfirmDialog
        open={!!confirmRestoreArchive}
        title={confirmRestoreArchive ? `Restore "${confirmRestoreArchive.document_name}"?` : ''}
        body="It'll reappear in its category. Risk-tier requirements, version history and reminder rules are preserved."
        confirmLabel="Restore"
        cancelLabel="Cancel"
        onConfirm={restoreFromArchive}
        onCancel={() => setConfirmRestoreArchive(null)} />

      {/* ===== Upload document ===== */}
      <UploadDocumentModal
        open={showUpload}
        onClose={() => setShowUpload(false)}
        onSaved={(status, notify) => {
          if (notify?.test) {
            flash(notify.failures?.length
              ? `Published, but the test email to ${notify.testEmail} failed.`
              : `Published — test email sent to ${notify.testEmail}. Suppliers were NOT notified.`);
          } else if (notify) {
            flash(notify.failures?.length
              ? `Published — notified ${notify.suppliersLogged} supplier(s), ${notify.failures.length} email(s) failed.`
              : `Published — notified ${notify.suppliersLogged} supplier(s).`);
          } else {
            flash(status === 'active' ? 'Document published.' : 'Draft saved.');
          }
          loadAll();
        }}
        categories={categories}
        badgeCategories={badgeCats}
        existingDocs={docs}
        currentUser={currentUser}
        onOpenRiskFlagConfig={onOpenRiskFlagConfig}
      />

      {/* ===== Manage categories ===== */}
      <ManageCategoriesPanel
        open={showManageCats}
        onClose={() => setShowManageCats(false)}
        categories={categories}
        badgeCategories={badgeCats}
        docs={docs}
        onSaved={loadAll}
      />
    </div>
  );
}

// ============================== sub-components ==============================

// Filter select (docs-filters §1) — a native <select> with a custom chevron
// and an active treatment when a non-default value is chosen. Native for
// accessibility; aria-label carries the concept since the placeholder can't.
function FilterSelect({ value, onChange, active, ariaLabel, children }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        aria-label={ariaLabel}
        className={`appearance-none font-['Metropolis'] text-[13px] font-semibold rounded-lg border pl-3.5 pr-8 py-2 outline-none cursor-pointer transition-colors ${active
          ? 'text-fusion-forest border-[#92D400] bg-[#92D400]/[0.06]'
          : 'text-ink border-line bg-white hover:border-[#DADCCD]'}`}>
        {children}
      </select>
      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-[#51534A]">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </span>
    </div>
  );
}

function SectionBlock({
  section, label, meta = {}, behaviour, docs, requirementsByKey, expandedFor, onToggleExpand,
  onConfigure, onToggleVisibility, onArchive, onDelete, onRestoreVersion, onRestoreArchive,
  onOpenRiskFlagConfig, onFlash, isActiveConfigDoc,
}) {
  const sub = meta.sub || '';
  const count = docs.length;
  // The compliance-locked banner / risk-tier link shows for any REQUIRED
  // category, not just the literal 'compliance' type.
  const isCompliance = behaviour === 'required';

  return (
    <div className="mb-6 last:mb-0">
      {/* Heading row */}
      <div className="flex items-center gap-3 mb-2.5">
        <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.25em] uppercase" style={{ color: meta.accent }}>
          {label}
        </div>
        <div className={`text-[9px] font-bold tracking-[0.15em] px-2 py-0.5 rounded-full ${count > 0
          ? 'bg-[#92D400]/10 text-fusion-forest'
          : 'bg-[#51534A]/15 text-[#51534A]'}`} style={count > 0 && section !== 'terms_conditions' && section !== 'supplier_enhancement' ? { color: meta.accent, background: `${meta.accent}1A` } : {}}>
          {count} {count === 1 ? (section === 'terms_conditions' ? 'active' : 'document') : (section === 'terms_conditions' ? 'active' : 'documents')}
        </div>
        <div className="flex-1 h-px bg-white" />
        <div className="font-['Metropolis'] text-[11px] text-[#51534A] flex-shrink-0">
          {sub}
          {isCompliance && (
            <>
              {' '}
              <button
                type="button"
                onClick={() => onOpenRiskFlagConfig?.()}
                className="text-[#2E8FA1] hover:underline bg-transparent border-none p-0 font-['Metropolis'] cursor-pointer">
                Risk Flag Configuration →
              </button>
            </>
          )}
        </div>
      </div>

      {/* The per-category lock note is now a single line on the group header
          above (docs-filters §6) — the repeated full-width banner is retired. */}

      {/* Empty state */}
      {count === 0 && (
        <div className="bg-white border border-line rounded p-5 font-['Metropolis'] text-xs text-[#51534A] italic">
          No {label.toLowerCase()} documents yet.
        </div>
      )}

      {/* Rows */}
      <div className="space-y-2">
        {docs.map(doc => (
          <DocumentRow
            key={doc.id}
            doc={doc}
            section={section}
            requirements={requirementsByKey[doc.compliance_doc_key]}
            expanded={expandedFor === doc.id}
            onToggleExpand={() => onToggleExpand(doc.id)}
            onConfigure={() => onConfigure(doc)}
            onToggleVisibility={(next) => onToggleVisibility(doc, next)}
            onArchive={() => onArchive(doc)}
            onDelete={onDelete ? () => onDelete(doc) : null}
            onRestoreArchive={onRestoreArchive ? () => onRestoreArchive(doc) : null}
            onRestoreVersion={(v) => onRestoreVersion(v)}
            onOpenRiskFlagConfig={onOpenRiskFlagConfig}
            onFlash={onFlash}
            isActiveConfigDoc={isActiveConfigDoc === doc.id} />
        ))}
      </div>
    </div>
  );
}

function DocumentRow({
  doc, section, requirements, expanded, onToggleExpand,
  onConfigure, onToggleVisibility, onArchive, onDelete, onRestoreArchive,
  onRestoreVersion, onOpenRiskFlagConfig, onFlash, isActiveConfigDoc,
}) {
  const navigate = useNavigate();
  const olderVersions = doc._olderVersions || [];
  const hasHistory = olderVersions.length > 0;
  // Hosted-form docs are authored in the prose editor, not attached. Until they
  // have content they're "Draft — not built" (and Risk Flag Config refuses the
  // tick, see LinkedToCell).
  const isForm = (doc.satisfied_by || '') === 'form';
  const formBuilt = isForm && isFormBuilt(doc.form_schema);
  const isPdfSign = (doc.satisfied_by || '') === 'pdf_sign';
  const pdfFieldsPlaced = isPdfSign && Array.isArray(doc.field_layout) && doc.field_layout.some(f => f?.type === 'signature');
  const isCompliance = section === 'compliance';
  const isInternal = section === 'internal_reference';
  const isArchived = section === 'archived';
  const isSupplierEnhancement = section === 'supplier_enhancement';
  const isHidden = !doc.visible_in_portal && (isSupplierEnhancement || section === 'terms_conditions');

  const isDraft = doc.status === 'draft';
  const portalState = (() => {
    if (isCompliance) return 'locked';
    if (isInternal || isArchived) return 'locked';
    if (doc.status === 'superseded') return 'locked';
    // A draft isn't live in the portal until published — lock the toggle so it
    // never reads as "Visible in Portal" while still a draft.
    if (isDraft) return 'locked';
    return doc.visible_in_portal ? 'on' : 'off';
  })();
  const portalLockedReason = (isCompliance && !isInternal)
    ? 'by-risk-tier'
    : (isDraft && !isInternal && !isArchived)
      ? 'draft'
      : 'category-locked';

  const cardBorderColor = isCompliance ? '#2E8FA1' : (hasHistory ? '#92D400' : '#E6EADB');
  const cardLeftBorder = (isCompliance || hasHistory) ? `3px solid ${cardBorderColor}` : `1px solid #E6EADB`;
  const opacity = (isHidden || isArchived) ? 0.78 : 1;

  return (
    <div
      className={`bg-white border border-line rounded ${isActiveConfigDoc ? 'ring-1 ring-active-green/40' : ''}`}
      style={{ borderLeft: cardLeftBorder, opacity }}>
      {/* Main row */}
      <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center px-5 py-4">
        {/* Info col */}
        <div className="min-w-0 flex items-center gap-3">
          <div className="w-9 h-9 rounded bg-[#2E8FA1]/10 border border-[#2E8FA1]/30 flex items-center justify-center flex-shrink-0">
            <FileIcon />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-['Metropolis'] text-sm font-bold text-ink truncate">{doc.document_name}</span>
              <StatusPill status={doc.status} />
              {isForm && !formBuilt && (
                <span className="font-['Metropolis'] text-[9px] font-bold tracking-[0.12em] uppercase rounded-md px-2 py-0.5 border whitespace-nowrap text-[#7A4E10] bg-[#E89A2E]/10 border-[#E89A2E]/40">
                  Not built
                </span>
              )}
              {doc.compliance_doc_key && METHOD_PILL[doc.satisfied_by || 'upload'] && (
                <span className={`font-['Metropolis'] text-[9px] font-bold tracking-[0.12em] uppercase rounded-md px-2 py-0.5 border whitespace-nowrap ${METHOD_PILL[doc.satisfied_by || 'upload'].cls}`}>
                  {METHOD_PILL[doc.satisfied_by || 'upload'].label}
                </span>
              )}
              {versionLabel(doc) && (
                // §5: a version number is a link to the version history
                // everywhere it appears — it was inert text before.
                <button
                  type="button"
                  onClick={() => navigate(`/documents/${doc.id}/versions`)}
                  title="Version history — every version and who signed what"
                  className="font-['JetBrains_Mono','ui-monospace',monospace] text-[10px] text-[#2E8FA1] bg-[#2E8FA1]/10 border border-[#2E8FA1]/30 px-1.5 py-0.5 rounded cursor-pointer hover:bg-[#2E8FA1]/20 hover:border-[#2E8FA1]/60 transition-colors">
                  {versionLabel(doc)}
                </button>
              )}
              {isHidden && (
                <span className="inline-flex items-center gap-1 text-[#51534A] text-[10px] font-bold tracking-[0.15em] uppercase">
                  <EyeOffIcon /> Hidden
                </span>
              )}
              {/* An unpublished edit of THIS live version (versioning §3a). The
                  live version is what suppliers still see until it's published. */}
              {doc._workingDraft && (
                <span className="font-['Metropolis'] text-[9px] font-bold tracking-[0.12em] uppercase rounded-md px-2 py-0.5 border whitespace-nowrap text-[#7A4E10] bg-[#E89A2E]/10 border-[#E89A2E]/40">
                  Draft v{doc._workingDraft.version_value} in progress
                </span>
              )}
            </div>
            <div className="font-['Metropolis'] text-[10px] text-[#51534A] mt-1 flex items-center gap-1.5 flex-wrap">
              {doc.file_type && <span>{doc.file_type}</span>}
              {formatFileSize(doc.file_size_kb) && (
                <>
                  <Dot />
                  <span>{formatFileSize(doc.file_size_kb)}</span>
                </>
              )}
              <Dot />
              <span>
                Uploaded {fmtDate(doc.created_at)}
                {doc.uploaded_by ? ' · staff' : ' · system'}
              </span>
              {hasHistory && (
                <>
                  <Dot />
                  <button
                    type="button"
                    onClick={onToggleExpand}
                    className="text-fusion-forest hover:underline bg-transparent border-none p-0 font-['Metropolis'] text-[10px] cursor-pointer">
                    {olderVersions.length} older {olderVersions.length === 1 ? 'version' : 'versions'} {expanded ? '↑' : '↓'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Linked-to */}
        <div className="flex-shrink-0">
          <LinkedToCell
            section={section}
            doc={doc}
            requirements={requirements}
            onOpenRiskFlagConfig={onOpenRiskFlagConfig}
            onFlash={onFlash} />
        </div>

        {/* Portal toggle */}
        <div className="flex-shrink-0">
          <PortalToggle
            state={portalState}
            lockedReason={portalLockedReason}
            onChange={(next) => onToggleVisibility(next)} />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {isForm && (
            <button
              type="button"
              onClick={() => navigate(`/documents/${doc.id}/build`)}
              className={`font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase px-3 py-2 rounded transition-colors ${formBuilt
                ? 'border border-line text-fusion-mid-grey bg-transparent hover:border-[#DADCCD]'
                : 'border border-[#E89A2E] text-[#7A4E10] bg-[#E89A2E]/10 hover:bg-[#E89A2E]/20'}`}>
              {formBuilt ? 'Edit form' : 'Build the form'}
            </button>
          )}
          {isPdfSign && (
            <button
              type="button"
              onClick={() => navigate(`/documents/${doc.id}/pdf-fields`)}
              className={`font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase px-3 py-2 rounded transition-colors ${pdfFieldsPlaced
                ? 'border border-line text-fusion-mid-grey bg-transparent hover:border-[#DADCCD]'
                : 'border border-[#E89A2E] text-[#7A4E10] bg-[#E89A2E]/10 hover:bg-[#E89A2E]/20'}`}>
              {pdfFieldsPlaced ? 'Edit fields' : 'Place the fields'}
            </button>
          )}
          <button
            type="button"
            onClick={onConfigure}
            className="font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase px-3 py-2 rounded border border-line text-fusion-mid-grey bg-transparent hover:border-[#DADCCD] transition-colors">
            Configure
          </button>
          <MoreMenu items={[
            doc.file_url && { label: 'View File', onClick: () => window.open(doc.file_url, '_blank') },
            doc.file_url && { label: 'Download', onClick: () => window.open(doc.file_url, '_blank') },
            !isArchived && { label: 'Archive', onClick: () => onArchive(), danger: true },
            isArchived && onRestoreArchive && { label: 'Restore', onClick: () => onRestoreArchive() },
            onDelete && { label: 'Delete', onClick: () => onDelete(), danger: true },
          ].filter(Boolean)} />
        </div>
      </div>

      {/* Version history */}
      {expanded && hasHistory && (
        <VersionHistory
          versions={olderVersions}
          onRestore={(v) => onRestoreVersion(v)} />
      )}
    </div>
  );
}

function LinkedToCell({ section, doc, requirements, onOpenRiskFlagConfig, onFlash }) {
  if (section === 'compliance') {
    // Reported as "these controls are locked". They never were — this button
    // opens the Risk Flag Configuration, where the tiers are actually set. The
    // PADLOCK was the whole problem: it says "you cannot change this", when the
    // truth is "change it over there". People saw the padlock and stopped.
    //
    // Now a sliders icon plus an explicit tooltip, so the control reads as what
    // it is: a way through to the setting, not a barrier.
    const tiers = formatRequiredTiers(requirements);
    return (
      <button
        type="button"
        onClick={() => onOpenRiskFlagConfig?.(doc.compliance_doc_key)}
        title={`Required for: ${tiers}. Opens Risk Flag Configuration, where this is set.`}
        aria-label={`Required for ${tiers}. Change in Risk Flag Configuration.`}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-[#2E8FA1]/30 bg-[#2E8FA1]/[0.06] text-[#2E8FA1] font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase hover:bg-[#2E8FA1]/10 hover:border-[#2E8FA1] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2E8FA1]">
        <SlidersIcon />
        {tiers}
      </button>
    );
  }
  if (section === 'terms_conditions') {
    return (
      <button
        type="button"
        onClick={() => onFlash?.('T&C signature flow is consumed by the supplier sign-up page.')}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-[#92D400]/30 bg-[#92D400]/[0.06] text-fusion-forest font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase hover:bg-active-green/10 transition-colors">
        <LinkIcon />
        T&C signature flow
      </button>
    );
  }
  if (section === 'supplier_enhancement') {
    return <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase text-[#51534A]">Optional</span>;
  }
  return <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase text-[#51534A]">Admin only</span>;
}

function VersionHistory({ versions, onRestore }) {
  return (
    <div className="bg-[#F7F9F0] border-t border-line px-5 py-4">
      <div className="font-['Metropolis'] text-[9px] font-bold tracking-[0.2em] uppercase text-[#51534A] mb-3">
        Version History
      </div>
      <div className="space-y-2">
        {versions.map(v => (
          <div key={v.id} className="grid grid-cols-[auto_1fr_auto] gap-3 items-center px-3 py-2 bg-white border border-line rounded">
            <div className="w-2 h-2 rounded-full bg-[#51534A] flex-shrink-0" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-['Metropolis'] text-xs font-semibold text-fusion-mid-grey truncate">{v.document_name}</span>
                {versionLabel(v) && (
                  <span className="font-['JetBrains_Mono','ui-monospace',monospace] text-[10px] text-[#2E8FA1] bg-[#2E8FA1]/10 border border-[#2E8FA1]/30 px-1.5 py-0.5 rounded">
                    {versionLabel(v)}
                  </span>
                )}
                <StatusPill status="superseded" />
              </div>
              <div className="font-['Metropolis'] text-[10px] text-[#51534A] mt-0.5">
                {v.superseded_at ? `Replaced ${fmtDate(v.superseded_at)}` : 'Superseded'}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {v.file_url && (
                <button
                  type="button"
                  onClick={() => window.open(v.file_url, '_blank')}
                  className="font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase px-2.5 py-1 rounded border border-line text-[#51534A] hover:text-ink hover:border-[#DADCCD] transition-colors">
                  View
                </button>
              )}
              <button
                type="button"
                onClick={() => onRestore(v)}
                className="font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase px-2.5 py-1 rounded border border-[#E89A2E]/40 text-[#7A4E10] hover:bg-[#E89A2E]/[0.06] transition-colors">
                Restore as Active
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const cfg = STATUS_PILL[status];
  if (!cfg) return null;
  return (
    <span className={`font-['Metropolis'] text-[9px] font-bold tracking-[0.15em] uppercase border px-1.5 py-0.5 rounded ${cfg.classes}`}>
      {cfg.label}
    </span>
  );
}

const Dot = () => <span className="w-[3px] h-[3px] bg-[#51534A] rounded-full inline-block" />;

// ============================== icons ==============================

const ICON_BASE = {
  width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round', strokeLinejoin: 'round',
  'aria-hidden': true,
};
const InfoIcon = () => (
  <svg {...ICON_BASE} width="16" height="16">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);
const PlusIcon = () => (
  <svg {...ICON_BASE} width="12" height="12" strokeWidth="3">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
// Adjustable-setting mark. Replaced the padlock on the compliance "required
// for" control: that control has always opened Risk Flag Configuration, so a
// padlock was telling people the opposite of the truth and they stopped
// clicking it.
const SlidersIcon = () => (
  <svg {...ICON_BASE} width="12" height="12">
    <line x1="4" y1="21" x2="4" y2="14" />
    <line x1="4" y1="10" x2="4" y2="3" />
    <line x1="12" y1="21" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12" y2="3" />
    <line x1="20" y1="21" x2="20" y2="16" />
    <line x1="20" y1="12" x2="20" y2="3" />
    <line x1="1" y1="14" x2="7" y2="14" />
    <line x1="9" y1="8" x2="15" y2="8" />
    <line x1="17" y1="16" x2="23" y2="16" />
  </svg>
);
const LinkIcon = () => (
  <svg {...ICON_BASE} width="12" height="12">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.72" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.72-1.72" />
  </svg>
);
const FileIcon = () => (
  <svg {...ICON_BASE} width="18" height="18" stroke="#2E8FA1">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);
const EyeOffIcon = () => (
  <svg {...ICON_BASE} width="11" height="11">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

